/**
 * "Auto enrichment for past leads" on the conference Overview tab: the
 * per-company queue for the automatic enrichment run (viq-backend
 * handlers/conferenceAutoEnrich.ts). Every call returns the show's company
 * lines, so the card refreshes from the answer.
 */
import { superadminFetch } from './dataProvider';

/** One company with access to the show. Counts only. */
export interface AutoEnrichCompanyLine {
  companyId: string;
  companyName: string;
  /** The company's "Auto enrichment" flag; the run needs it on. */
  autoEnrich: boolean;
  archived: boolean;
  /** Set while this company's leads at the show are queued. */
  backfill: { requestedAt: string; requestedBy: string } | null;
  leads: number;
  withPhone: number;
  /** Waiting for the automatic run right now. */
  queued: number;
  /** Checked by the automatic run, and the lead has a phone now. */
  filledByAuto: number;
  /** Checked by the automatic run, and still no phone. */
  checkedNoPhone: number;
}

const path = (conferenceId: string) => `/v1/superadmin/conferences/${encodeURIComponent(conferenceId)}/auto-enrichment`;

/** GET …/auto-enrichment */
export async function getConferenceAutoEnrichment(conferenceId: string): Promise<AutoEnrichCompanyLine[]> {
  return (await superadminFetch<{ companies: AutoEnrichCompanyLine[] }>(path(conferenceId))).companies;
}

/** POST …/auto-enrichment `{ companyId }`: queue the company's leads at the show. */
export async function startConferenceAutoEnrichment(conferenceId: string, companyId: string): Promise<AutoEnrichCompanyLine[]> {
  return (await superadminFetch<{ companies: AutoEnrichCompanyLine[] }>(path(conferenceId), {
    method: 'POST', body: JSON.stringify({ companyId }),
  })).companies;
}

/** DELETE …/auto-enrichment/{companyId}: take them back out of the queue. */
export async function stopConferenceAutoEnrichment(conferenceId: string, companyId: string): Promise<AutoEnrichCompanyLine[]> {
  return (await superadminFetch<{ companies: AutoEnrichCompanyLine[] }>(
    `${path(conferenceId)}/${encodeURIComponent(companyId)}`, { method: 'DELETE' },
  )).companies;
}
